import { createContext, useContext, useEffect, useRef } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

const EmptyNavigationContext = createContext(null);

// BrowserRouter does not expose the data-router blocker. Guard its navigation
// adapter, native document exits, and indexed browser history traversal.
export default function useAdminTrainingLibraryGuard(dirty, message, busy = false) {
  const context = useContext(UNSAFE_NavigationContext || EmptyNavigationContext);
  const current = useRef({ dirty, message, busy });
  current.current = { dirty, message, busy };
  useEffect(() => {
    let approvedExit = false;
    const mayLeave = () => !current.current.busy && (approvedExit || !current.current.dirty || window.confirm(current.current.message));
    const beforeLeave = (event) => {
      if (!mayLeave()) event.preventDefault();
      else approvedExit = true;
    };
    const beforeUnload = (event) => {
      if (!current.current.dirty && !current.current.busy) return;
      event.preventDefault(); event.returnValue = "";
    };
    const navigator = context?.navigator;
    const restore = [];
    for (const key of ["push", "replace", "go"]) {
      if (!navigator?.[key]) continue;
      const original = navigator[key];
      const guarded = function (...args) { if (mayLeave()) return original.apply(this, args); };
      navigator[key] = guarded;
      restore.push(() => { if (navigator[key] === guarded) navigator[key] = original; });
    }
    let acceptedIndex = window.history.state?.idx;
    let restoring = false;
    const pop = (event) => {
      const next = event.state?.idx;
      if (restoring) { restoring = false; event.stopImmediatePropagation(); return; }
      if (Number.isInteger(next) && Number.isInteger(acceptedIndex) && next !== acceptedIndex && !mayLeave()) {
        event.stopImmediatePropagation(); restoring = true; window.history.go(acceptedIndex - next);
      } else acceptedIndex = next;
    };
    const anchor = (event) => {
      const link = event.target.closest?.("a[href]");
      if (!link || link.target === "_blank" || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      if (link.target && link.target !== "_self") return;
      if (!mayLeave()) { event.preventDefault(); event.stopPropagation(); }
    };
    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("pc-training-library-before-leave", beforeLeave);
    window.addEventListener("popstate", pop, true);
    document.addEventListener("click", anchor, true);
    return () => {
      restore.forEach((fn) => fn());
      window.removeEventListener("beforeunload", beforeUnload);
      window.removeEventListener("pc-training-library-before-leave", beforeLeave);
      window.removeEventListener("popstate", pop, true);
      document.removeEventListener("click", anchor, true);
    };
  }, [context?.navigator]);
}
